import router from '@/router/router'
import { getIsAuthenticated } from '@/StoreWeb/js/composables/login-utils'
import * as cartActionTypes from '@/StoreWeb/store/modules/cart/action-types'
import * as cartMutationTypes from '@/StoreWeb/store/modules/cart/mutation-types'
import * as catalogActionTypes from '@/StoreWeb/store/modules/catalog/action-types'
import * as userMutationTypes from '@/StoreWeb/store/modules/user/mutation-types'
import store from '@/StoreWeb/store/store'
import config from 'config'
import { isEmpty } from 'global-utils'
import Cookies from 'js-cookie'
import { computed, ref } from 'vue'

const cookieDuration = config.features.fare_media_selection.cookie_duration * 1000
const safetyDelay = config.features.fare_media_selection.cookie_refresh_safety_delay * 1000
// Init to 10 min of inactivity
const inactivityTime = 10 * 60 * 1000
// Listen every 1s
const debounceTime = 1000
const activityEvents = ['mousemove', 'click', 'scroll', 'keydown', 'touchstart', 'touchmove']
const inactivityTimeout = ref(null)
const debounceTimeout = ref(null)
const selectedFareMediaTimeout = ref(null)
const showConfirmResetCartModal = ref(false)

export function useSession () {
    const getCart = computed(() => store.getters.getCart)

    /* ------------------------------------------------------------- */
    /*                           SESSION                             */
    /* ------------------------------------------------------------- */
    function sessionExpired ({ withConfirmResetCartModal = false } = {}) {
        resetSelectedFareMedia()
        resetCartEmail()
        resetCart({ withConfirmResetCartModal })
        cancelInactivityEvents()
    }

    function changeFareMedia (fareMedia) {
        if (!getIsAuthenticated()) {
            const selectedFareMediaCookie = getSelectedFareMediaCookie()

            if (!isEmpty(fareMedia) && !isEmpty(selectedFareMediaCookie) && fareMedia.fareMediaId !== selectedFareMediaCookie.fareMediaId) {
                // A new fareMedia has been introduced so we clean the previous session
                sessionExpired({ withConfirmResetCartModal: true })
            }
        }

        // Save the new fareMedia in the cookies and store
        setSelectedFareMedia(fareMedia)
    }

    /* ------------------------------------------------------------- */
    /*                           CATALOG                             */
    /* ------------------------------------------------------------- */
    function refreshCatalog ({ withRedirection = false } = {}) {
        store.dispatch(catalogActionTypes.GET_CATALOG)

        if (withRedirection) {
            router.push({ name: 'catalog' })
        }
    }

    /* ------------------------------------------------------------- */
    /*                             CART                              */
    /* ------------------------------------------------------------- */
    async function resetCart ({ withConfirmResetCartModal = false } = {}) {
        const cart = getCart.value

        if (!isEmpty(cart) && !cart.empty) {
            try {
                await store.dispatch(cartActionTypes.DELETE_CART, {
                    id: cart.id
                })
                if (withConfirmResetCartModal) {
                    showConfirmResetCartModal.value = true
                }
                return true
            } catch (error) {
                return false
            }
        }
    }

    /* ------------------------------------------------------------- */
    /*                          CART EMAIL                           */
    /* ------------------------------------------------------------- */
    function resetCartEmail () {
        store.commit(cartMutationTypes.SET_ADD_TO_CART_EMAIL, null)
        removeCartEmailCookie()
    }

    /* ------------------------------------------------------------- */
    /*                          FARE MEDIA                           */
    /* ------------------------------------------------------------- */
    function setSelectedFareMedia (fareMedia) {
        if (fareMedia && (fareMedia.id || fareMedia.fareMediaId)) {
            store.commit(userMutationTypes.SET_SELECTED_FARE_MEDIA, fareMedia.fareMediaId === 'ALL' ? null : fareMedia)

            const fareMediaCookie = {
                fareMediaId: fareMedia.id || fareMedia.fareMediaId,
                ...(fareMedia.providerId && { providerId: fareMedia.providerId }),
                ...(fareMedia.providerUserId && { providerUserId: fareMedia.providerUserId }),
                ...(fareMedia.providerUserExternalId && { providerUserExternalId: fareMedia.providerUserExternalId }),
                ...(fareMedia.isTemporary && { isTemporary: fareMedia.isTemporary })
            }
            setSelectedFareMediaCookie(fareMediaCookie)

            if (!getIsAuthenticated()) {
                initInactivityEvents()
            }
        } else {
            resetSelectedFareMedia()
        }
    }

    function resetSelectedFareMedia () {
        store.commit(userMutationTypes.SET_SELECTED_FARE_MEDIA, null)
        removeSelectedFareMediaCookie()
    }

    function autoSetSelectedFareMedia () {
        const { query } = store.state.currentRoute ?? {}
        const resetSelectedFareMediaFilter = query?.resetSelectedFareMediaFilter === 'true'
        const lastSelectedFareMediaId = store.state.userModule.wallet.lastSelectedFareMediaId ?? null
        const selectedFareMediaCookie = getSelectedFareMediaCookie()
        const walletFareMedias = store.getters.getWalletFareMedias
        const addToCartFlow = JSON.parse(localStorage.getItem('addToCartFlow') || 'null')

        let chosenFareMediaId = selectedFareMediaCookie?.fareMediaId || lastSelectedFareMediaId || 'ALL'

        if (addToCartFlow?.catalogContext?.condition?.fareMediaId) {
            chosenFareMediaId = addToCartFlow.catalogContext.condition.fareMediaId
        } else if (addToCartFlow) {
            chosenFareMediaId = 'ALL'
        }

        if (resetSelectedFareMediaFilter) {
            chosenFareMediaId = 'ALL'
        }

        if (chosenFareMediaId === 'ALL' || !isEmpty(store.state.userModule.selectedFareMedia)) {
            changeFareMedia({ fareMediaId: 'ALL' })
            return
        }

        const selectedFareMedia = walletFareMedias.find(({ id }) => id === chosenFareMediaId) || walletFareMedias[0]

        if (selectedFareMedia) {
            changeFareMedia(selectedFareMedia)
        }
    }

    /* ------------------------------------------------------------- */
    /*                          INACTIVITY                           */
    /* ------------------------------------------------------------- */
    function initInactivityEvents () {
        const sessionExpires = localStorage.getItem('sessionExpires')
        const now = Date.now()

        if (sessionExpires && now > sessionExpires) {
            sessionExpired()
        }

        activeInactivityEvents()
        resetInactivityTimeout()
    }

    function cancelInactivityEvents () {
        clearTimeout(inactivityTimeout.value)

        activityEvents.forEach(event => {
            document.removeEventListener(event, debounceResetTimeout)
        })

        setTimeout(() => {
            localStorage.removeItem('sessionExpires')
        }, debounceTime + 250)
    }

    function activeInactivityEvents () {
        activityEvents.forEach(event => {
            document.addEventListener(event, debounceResetTimeout)
        })
    }

    // Debounce the resetInactivityTimeout function to avoid too many calls
    function debounceResetTimeout () {
        clearTimeout(debounceTimeout.value)
        debounceTimeout.value = setTimeout(resetInactivityTimeout, debounceTime)
    }

    function resetInactivityTimeout () {
        const now = Date.now()

        clearTimeout(inactivityTimeout)
        inactivityTimeout.value = setTimeout(sessionExpired, inactivityTime)
        localStorage.setItem('sessionExpires', now + inactivityTime)
    }

    /* ------------------------------------------------------------- */
    /*                            COOKIES                            */
    /* ------------------------------------------------------------- */
    function getSelectedFareMediaCookie () {
        let selectedFareMediaCookie = Cookies.get('selectedFareMedia')

        if (!isEmpty(selectedFareMediaCookie)) {
            selectedFareMediaCookie = JSON.parse(selectedFareMediaCookie)

            if (selectedFareMediaCookie.expires) {
                delete selectedFareMediaCookie.expires
            }

            return selectedFareMediaCookie
        }

        return null
    }

    function setSelectedFareMediaCookie (value) {
        const expiresTimestamp = Date.now() + cookieDuration

        value.expires = expiresTimestamp

        Cookies.set('selectedFareMedia', JSON.stringify(value), {
            expires: new Date(expiresTimestamp)
        })

        scheduleRefreshSelectedFareMedia()
    }

    function removeCartEmailCookie () {
        Cookies.remove('cartEmail', {
            domain: config?.userweb?.cookies_domain ?? window.location.hostname,
            path: '/'
        })
    }

    function refreshSelectedFareMediaCookie () {
        const value = getSelectedFareMediaCookie()

        if (!isEmpty(value)) {
            const expiresTimestamp = Date.now() + cookieDuration

            value.expires = expiresTimestamp

            setSelectedFareMediaCookie(value)
        }
    }

    function removeSelectedFareMediaCookie () {
        Cookies.remove('selectedFareMedia')
        clearTimeout(selectedFareMediaTimeout.value)
    }

    function getSelectedFareMediaCookieRemainingTime () {
        let selectedFareMediaCookie = Cookies.get('selectedFareMedia')

        if (!isEmpty(selectedFareMediaCookie)) {
            selectedFareMediaCookie = JSON.parse(selectedFareMediaCookie)

            if (!isEmpty(selectedFareMediaCookie.expires)) {
                const remainingTime = selectedFareMediaCookie.expires - Date.now()

                return remainingTime > 0 ? remainingTime : 0
            }
        }

        return -1
    }

    function scheduleRefreshSelectedFareMedia () {
        const remainingTime = getSelectedFareMediaCookieRemainingTime()

        if (!isNaN(remainingTime) && remainingTime > safetyDelay) {
            const delay = Math.floor(remainingTime - safetyDelay)

            clearTimeout(selectedFareMediaTimeout.value)
            selectedFareMediaTimeout.value = setTimeout(() => {
                refreshSelectedFareMediaCookie()
            }, delay)
        } else if (!isNaN(remainingTime) && remainingTime > 0) {
            refreshSelectedFareMediaCookie()
        }
    }

    /* ------------------------------------------------------------- */
    /*                        EXPOSED METHODS                        */
    /* ------------------------------------------------------------- */
    return {
        autoSetSelectedFareMedia,
        cancelInactivityEvents,
        changeFareMedia,
        getSelectedFareMediaCookie,
        refreshCatalog,
        removeCartEmailCookie,
        resetCart,
        resetSelectedFareMedia,
        removeSelectedFareMediaCookie,
        scheduleRefreshSelectedFareMedia,
        sessionExpired,
        showConfirmResetCartModal
    }
}
